import React, { useState, useRef } from 'react';
import { recipes, categoryMeta, RecipeCategory } from '../data/recipes';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, ExternalLink, Play } from 'lucide-react';
const ALL_CATEGORIES = Object.keys(categoryMeta) as RecipeCategory[];
export function RecipeCategories() {
  const [activeTab, setActiveTab] = useState<RecipeCategory>('fusion');
  const scrollRef = useRef<HTMLDivElement>(null);
  const filteredRecipes = recipes.filter(r => r.categories.includes(activeTab));
  return <section id="categories" className="py-20 px-4 bg-[#fffbf5]" data-id="element-109">
      <div className="max-w-7xl mx-auto" data-id="element-110">
        <div className="text-center mb-12" data-id="element-111">
          <h2 className="text-3xl md:text-5xl font-bold text-stone-900 mb-4" style={{
          fontFamily: 'Poppins, sans-serif'
        }} data-id="element-112">
            Browse by Category 🍽️
          </h2>
          <p className="text-stone-500 text-lg max-w-xl mx-auto" data-id="element-113">
            All recipes are from the{' '}
            <span className="font-bold text-green-600" data-id="element-114">BeVegToday</span> YouTube
            channel.
          </p>
        </div>

        {/* Scrollable Tab Bar */}
        <div ref={scrollRef} className="flex gap-2 overflow-x-auto pb-4 mb-10 scrollbar-hide snap-x" data-id="element-115">
          {ALL_CATEGORIES.map(cat => {
          const meta = categoryMeta[cat];
          const count = recipes.filter(r => r.categories.includes(cat)).length;
          return <button key={cat} onClick={() => setActiveTab(cat)} className={`flex-shrink-0 snap-start flex items-center gap-2 px-4 py-2.5 rounded-full font-semibold text-sm transition-all whitespace-nowrap ${activeTab === cat ? 'bg-green-600 text-white shadow-lg shadow-green-600/30 scale-105' : 'bg-white text-stone-600 border border-stone-200 hover:border-green-300 hover:text-green-700'}`} data-id="element-116">
                <span data-id="element-117">{meta.emoji}</span>
                <span data-id="element-118">{meta.label}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${activeTab === cat ? 'bg-white/20 text-white' : 'bg-stone-100 text-stone-500'}`} data-id="element-119">
                  {count}
                </span>
              </button>;
        })}
        </div>

        {/* Category Header */}
        <AnimatePresence mode="wait" data-id="element-120">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} data-id="element-121">
            <div className="flex items-center gap-3 mb-8" data-id="element-122">
              <span className="text-4xl" data-id="element-123">{categoryMeta[activeTab].emoji}</span>
              <div data-id="element-124">
                <h3 className="text-2xl font-bold text-stone-900" style={{
                fontFamily: 'Poppins, sans-serif'
              }} data-id="element-125">
                  {categoryMeta[activeTab].label}
                </h3>
                <p className="text-stone-500 text-sm" data-id="element-126">
                  {categoryMeta[activeTab].description} ·{' '}
                  {filteredRecipes.length} recipes
                </p>
              </div>
            </div>

            {filteredRecipes.length === 0 ? <div className="text-center py-16 text-stone-400" data-id="element-127">
                <p className="text-xl" data-id="element-128">No recipes in this category yet.</p>
              </div> : <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-129">
                {filteredRecipes.map((recipe, index) => <motion.a key={recipe.id} href={recipe.videoUrl} target="_blank" rel="noopener noreferrer" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.06,
              duration: 0.35
            }} className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow border border-stone-100 flex flex-col" data-id="element-130">
                    {/* Thumbnail */}
                    <div className="relative aspect-video overflow-hidden bg-stone-100" data-id="element-131">
                      <img src={recipe.thumbnail} alt={recipe.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" onError={e => {
                  ;
                  (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${recipe.videoId}/hqdefault.jpg`;
                }} data-id="element-132" />
                      {/* Play overlay */}
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-133">
                        <div className="bg-red-600 text-white p-3 rounded-full transform scale-90 group-hover:scale-100 transition-transform shadow-lg" data-id="element-134">
                          <Play size={20} fill="white" data-id="element-135" />
                        </div>
                      </div>
                      {/* Badges */}
                      <div className="absolute top-2 left-2 flex gap-1.5" data-id="element-136">
                        {recipe.isVrat && <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-137">
                            🙏 Vrat
                          </span>}
                        {recipe.isNoOnionGarlic && <span className="bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-138">
                            Jain ✓
                          </span>}
                      </div>
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs font-bold px-2 py-0.5 rounded" data-id="element-139">
                        ▶ Watch
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 flex-1 flex flex-col" data-id="element-140">
                      <h3 className="font-bold text-stone-900 mb-1 line-clamp-2 leading-snug" style={{
                  fontFamily: 'Poppins, sans-serif'
                }} data-id="element-141">
                        {recipe.name}
                      </h3>
                      {recipe.nameHindi && <p className="text-stone-400 text-xs mb-2" data-id="element-142">
                          {recipe.nameHindi}
                        </p>}
                      <p className="text-stone-500 text-sm line-clamp-2 mb-3 flex-1" data-id="element-143">
                        {recipe.description}
                      </p>

                      <div className="flex items-center justify-between pt-3 border-t border-stone-100" data-id="element-144">
                        <span className="flex items-center gap-1 text-stone-500 text-sm" data-id="element-145">
                          <Clock size={14} data-id="element-146" /> {recipe.cookTime} min
                        </span>
                        <span className="text-xs font-semibold text-stone-400 uppercase tracking-wide" data-id="element-147">
                          {recipe.cuisine}
                        </span>
                        <span className="flex items-center gap-1 text-red-500 text-xs font-bold hover:text-red-600" data-id="element-148">
                          <ExternalLink size={12} data-id="element-149" /> YouTube
                        </span>
                      </div>
                    </div>
                  </motion.a>)}
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}